import {
  AlertTriangle,
  Boxes,
  Briefcase,
  Building2,
  CreditCard,
  CircleDot,
  ClipboardList,
  Construction,
  FileText,
  Files,
  Flag,
  Handshake,
  HardHat,
  KeyRound,
  Landmark,
  Layers,
  ListChecks,
  Package,
  Receipt,
  Ruler,
  Shapes,
  SlidersHorizontal,
  Tags,
  Timer,
  UserCog,
  Users,
  Wallet,
  Wrench,
  type LucideIcon,
} from "lucide-react";

import type { Permission } from "@/types";

import type {
  SidebarNavContext,
  SidebarNavGroupItem,
  SidebarNavItem,
  SidebarNavLinkItem,
  SidebarNavSection,
} from "@/navigation/types";
import {
  filterSidebarGroupChildren,
  flattenSidebarNavLinks,
  firstGroupLink,
} from "@/navigation/navItems";

const SETTINGS_BOTTOM_NAV_PRIMARY_COUNT = 4;

/** Mobile “Mais” sheet entry — preserves groups so siblings stay labeled. */
export type SettingsBottomNavMoreItem = SidebarNavLinkItem | SidebarNavGroupItem;

const MATERIALS_NAV = {
  kind: "group" as const,
  labelKey: "nav.materials",
  icon: Package,
  permission: "settings.materials" as const,
  items: [
    {
      kind: "link" as const,
      to: "/settings/materials/measurement-units",
      end: true,
      labelKey: "settings.materials.measurementUnits",
      icon: Ruler,
    },
    {
      kind: "link" as const,
      to: "/settings/materials/categories",
      end: true,
      labelKey: "settings.materials.categories",
      icon: Layers,
    },
    {
      kind: "link" as const,
      to: "/settings/materials",
      end: true,
      labelKey: "settings.materials.list",
      icon: Package,
    },
  ],
};

const EQUIPMENTS_NAV = {
  kind: "group" as const,
  labelKey: "nav.equipments",
  icon: Wrench,
  permission: "settings.equipments" as const,
  items: [
    {
      kind: "link" as const,
      to: "/settings/equipments/categories",
      end: true,
      labelKey: "settings.equipments.categories",
      icon: Boxes,
    },
    {
      kind: "link" as const,
      to: "/settings/equipments",
      end: true,
      labelKey: "settings.equipments.list",
      icon: Wrench,
    },
  ],
};

const WORKERS_NAV = {
  kind: "group" as const,
  labelKey: "nav.workers",
  icon: HardHat,
  permission: "settings.workers" as const,
  items: [
    {
      kind: "link" as const,
      to: "/settings/workers/roles",
      end: true,
      labelKey: "settings.workers.roles",
      icon: Briefcase,
    },
    {
      kind: "link" as const,
      to: "/settings/workers",
      end: true,
      labelKey: "settings.workers.list",
      icon: HardHat,
    },
  ],
};

const LOOKUPS_NAV = {
  kind: "group" as const,
  labelKey: "nav.classifications",
  icon: Shapes,
  permission: "settings.lookups" as const,
  items: [
    {
      kind: "link" as const,
      to: "/settings/project-priorities",
      end: true,
      labelKey: "nav.projectPriorities",
      icon: Flag,
    },
    {
      kind: "link" as const,
      to: "/settings/project-types",
      end: true,
      labelKey: "nav.projectTypes",
      icon: Construction,
    },
    {
      kind: "link" as const,
      to: "/settings/project-statuses",
      end: true,
      labelKey: "nav.projectStatuses",
      icon: CircleDot,
    },
    {
      kind: "link" as const,
      to: "/settings/project-budget-statuses",
      end: true,
      labelKey: "nav.projectBudgetStatuses",
      icon: Wallet,
    },
    {
      kind: "link" as const,
      to: "/settings/project-client-budget-statuses",
      end: true,
      labelKey: "nav.projectClientBudgetStatuses",
      icon: Receipt,
    },
    {
      kind: "link" as const,
      to: "/settings/project-service-fronts-deadline-statuses",
      end: true,
      labelKey: "nav.serviceFrontDeadlineStatuses",
      icon: Timer,
    },
  ],
};

const FINANCEIRO_NAV: SidebarNavGroupItem = {
  kind: "group",
  labelKey: "nav.financeLookups",
  icon: Landmark,
  permission: "settings.financeLookups",
  items: [
    {
      kind: "link",
      to: "/settings/financial-categories",
      end: true,
      labelKey: "settings.finance.categories",
      icon: Layers,
    },
    {
      kind: "link",
      to: "/settings/payment-methods",
      end: true,
      labelKey: "settings.finance.paymentMethods",
      icon: Wallet,
    },
  ],
};

/** Company Settings — organization administration. */
const EMPRESA_NAV: SidebarNavItem[] = [
  {
    kind: "link",
    permission: "settings.organization",
    to: "/settings/general",
    end: true,
    labelKey: "settings.nav.general",
    icon: SlidersHorizontal,
  },
  {
    kind: "link",
    permission: "settings.organization",
    to: "/settings/organization",
    end: true,
    labelKey: "settings.nav.organization",
    icon: Building2,
  },
  {
    kind: "link",
    to: "/settings/billing",
    end: true,
    labelKey: "billing.title",
    icon: CreditCard,
  },
  {
    kind: "link",
    permission: "settings.users",
    to: "/settings/users",
    end: true,
    labelKey: "settings.nav.users",
    icon: UserCog,
  },
  {
    kind: "link",
    permission: "templates.manage",
    to: "/settings/permissions",
    end: true,
    labelKey: "settings.nav.permissions",
    icon: KeyRound,
  },
];

/** Operational registries — partners, finance lookups, catalog, crew, and project classifications. */
const CADASTROS_NAV: SidebarNavItem[] = [
  {
    kind: "link",
    permission: "settings.partners",
    to: "/settings/partners",
    end: true,
    labelKey: "nav.partners",
    icon: Handshake,
  },
  FINANCEIRO_NAV,
  {
    kind: "link",
    permission: "settings.fiscal",
    to: "/settings/service-tax-codes",
    end: true,
    labelKey: "nav.serviceTaxCodes",
    icon: Receipt,
  },
  MATERIALS_NAV,
  EQUIPMENTS_NAV,
  WORKERS_NAV,
  LOOKUPS_NAV,
];

/** Tool Settings — per-module configuration and taxonomies. */
const FERRAMENTAS_NAV: SidebarNavItem[] = [
  {
    kind: "link",
    permission: "project.projectReport.manageModels",
    to: "/settings/diary-report-models",
    end: true,
    labelKey: "diaryReports.settingsTitle",
    icon: Files,
  },
  {
    kind: "link",
    permission: "settings.serviceFrontModels",
    to: "/settings/project-service-fronts-models",
    end: true,
    labelKey: "nav.serviceFrontModels",
    icon: Users,
  },
  {
    kind: "link",
    permission: "settings.checklistModels",
    to: "/settings/checklist-models",
    end: true,
    labelKey: "nav.checklistModels",
    icon: ListChecks,
  },
  {
    kind: "link",
    permission: "settings.occurrences",
    to: "/settings/occurrences",
    end: true,
    labelKey: "nav.occurrences",
    icon: AlertTriangle,
  },
  {
    kind: "link",
    permission: "settings.documentCategories",
    to: "/settings/document-categories",
    end: true,
    labelKey: "nav.documentCategories",
    icon: FileText,
  },
  {
    kind: "link",
    permission: "settings.photoTags",
    to: "/settings/photo-tags",
    end: true,
    labelKey: "nav.photoTags",
    icon: Tags,
  },
];

const SETTINGS_SIDEBAR_SECTIONS: Array<SidebarNavSection & { icon: LucideIcon }> = [
  { titleKey: "settings.sections.empresa", icon: Building2, items: EMPRESA_NAV },
  { titleKey: "settings.sections.cadastros", icon: ClipboardList, items: CADASTROS_NAV },
  { titleKey: "settings.sections.ferramentas", icon: Wrench, items: FERRAMENTAS_NAV },
];

function filterByPermission(
  items: SidebarNavItem[],
  can: SidebarNavContext["can"],
): SidebarNavItem[] {
  return items
    .map((item) => {
      if (item.kind === "soon") {
        return !item.permission || can(item.permission) ? item : null;
      }
      if (item.kind === "link") {
        return !item.permission || can(item.permission) ? item : null;
      }
      if (item.permission && !can(item.permission)) return null;
      const subItems = filterSidebarGroupChildren(item.items, can);
      if (subItems.length === 0) return null;
      return { ...item, items: subItems };
    })
    .filter((item): item is SidebarNavItem => item != null);
}

function firstSettingsPathFromItem(item: SidebarNavItem): string | null {
  if (item.kind === "link") {
    const match = item.to.match(/^\/settings\/(.+)$/);
    return match?.[1] ?? null;
  }
  if (item.kind === "group") {
    const first = firstGroupLink(item);
    if (!first) return null;
    const match = first.to.match(/^\/settings\/(.+)$/);
    return match?.[1] ?? null;
  }
  return null;
}

/**
 * Relabel the canonical “list” child to the parent group name so the sidebar
 * shows “Materiais” / “Equipamentos” / “Colaboradores” instead of bare “Lista”.
 */
function withGroupListLabels(items: SidebarNavItem[]): SidebarNavItem[] {
  return items.map((item) => {
    if (item.kind !== "group") return item;
    return {
      ...item,
      items: item.items.map((sub) => {
        if (sub.kind === "group") {
          return {
            ...sub,
            items: sub.items.map((leaf) =>
              leaf.kind === "link" && leaf.labelKey.endsWith(".list")
                ? { ...leaf, labelKey: sub.labelKey }
                : leaf,
            ),
          };
        }
        return sub.labelKey.endsWith(".list") ? { ...sub, labelKey: item.labelKey } : sub;
      }),
    };
  });
}

export function resolveSettingsSidebarNav({
  can,
  showOrgBilling = false,
}: SidebarNavContext): SidebarNavSection[] {
  const items = SETTINGS_SIDEBAR_SECTIONS.map((section) => {
    const filtered = withGroupListLabels(
      filterByPermission(section.items, can).filter((item) => {
        if (item.kind === "link" && item.to === "/settings/billing") return showOrgBilling;
        return true;
      }),
    );
    return {
      kind: "group" as const,
      labelKey: section.titleKey,
      icon: section.icon,
      items: filtered.flatMap((item): SidebarNavGroupItem["items"] => {
        if (item.kind === "link") {
          return [
            {
              kind: "link" as const,
              to: item.to,
              end: item.end,
              labelKey: item.labelKey,
              icon: item.icon,
              permission: item.permission,
            },
          ];
        }
        if (item.kind === "group") return [item];
        return [];
      }),
    };
  }).filter((group) => group.items.length > 0);

  if (items.length === 0) return [];

  return [{ titleKey: "settings.sections.empresa", items }];
}

function resolveSettingsBottomNavLinks(
  can: (permission: Permission) => boolean,
  showOrgBilling = false,
): SidebarNavLinkItem[] {
  const sections = resolveSettingsSidebarNav({ params: {}, can, showOrgBilling });
  return flattenSidebarNavLinks(sections.flatMap((section) => section.items));
}

/** First four settings routes — mobile bottom bar. */
export function getSettingsBottomNavPrimary(
  can: (permission: Permission) => boolean,
  options?: { showOrgBilling?: boolean },
): SidebarNavLinkItem[] {
  return resolveSettingsBottomNavLinks(can, options?.showOrgBilling).slice(
    0,
    SETTINGS_BOTTOM_NAV_PRIMARY_COUNT,
  );
}

/**
 * Remaining settings routes for the mobile “Mais” sheet.
 * Preserves group structure (unlike primary, which is a flat icon bar).
 */
export function getSettingsBottomNavMore(
  can: (permission: Permission) => boolean,
  options?: { showOrgBilling?: boolean },
): SettingsBottomNavMoreItem[] {
  const showOrgBilling = options?.showOrgBilling ?? false;
  const sections = resolveSettingsSidebarNav({ params: {}, can, showOrgBilling });
  const topLevel = sections.flatMap((section) => section.items);
  const primaryTos = new Set(
    resolveSettingsBottomNavLinks(can, showOrgBilling)
      .slice(0, SETTINGS_BOTTOM_NAV_PRIMARY_COUNT)
      .map((item) => item.to),
  );

  const more: SettingsBottomNavMoreItem[] = [];

  for (const item of topLevel) {
    if (item.kind === "soon") continue;

    if (item.kind === "link") {
      if (!primaryTos.has(item.to)) more.push(item);
      continue;
    }

    const remaining = item.items
      .map((sub) => {
        if (sub.kind === "group") {
          const nested = sub.items.filter(
            (leaf) => leaf.kind === "link" && !primaryTos.has(leaf.to),
          );
          if (nested.length === 0) return null;
          return { ...sub, items: nested };
        }
        return primaryTos.has(sub.to) ? null : sub;
      })
      .filter((sub): sub is NonNullable<typeof sub> => sub != null);

    if (remaining.length === 0) continue;
    more.push({ ...item, items: remaining });
  }

  return more;
}

/** First settings child route segment the user may access (for `/settings` index redirect). */
export function resolveFirstSettingsPath(ctx: SidebarNavContext): string {
  for (const section of resolveSettingsSidebarNav(ctx)) {
    for (const item of section.items) {
      const path = firstSettingsPathFromItem(item);
      if (path) return path;
    }
  }
  return "materials";
}

export type SettingsHomeCard = {
  to: string;
  labelKey: string;
  descriptionKey: string;
  icon: LucideIcon;
  permission: Permission;
  requireOrgBilling?: boolean;
};

export type SettingsHomeSection = {
  titleKey: string;
  cards: SettingsHomeCard[];
};

const SETTINGS_HOME_SECTIONS: SettingsHomeSection[] = [
  {
    titleKey: "settings.sections.empresa",
    cards: [
      {
        to: "/settings/general",
        labelKey: "settings.nav.general",
        descriptionKey: "settings.home.descriptions.general",
        icon: SlidersHorizontal,
        permission: "settings.organization",
      },
      {
        to: "/settings/billing",
        labelKey: "billing.title",
        descriptionKey: "billing.settingsDescription",
        icon: CreditCard,
        permission: "settings.organization",
        requireOrgBilling: true,
      },
    ],
  },
  {
    titleKey: "settings.sections.cadastros",
    cards: [
      {
        to: "/settings/partners",
        labelKey: "nav.partners",
        descriptionKey: "settings.home.descriptions.partners",
        icon: Handshake,
        permission: "settings.partners",
      },
      {
        to: "/settings/financial-categories",
        labelKey: "nav.financeLookups",
        descriptionKey: "settings.home.descriptions.financeLookups",
        icon: Landmark,
        permission: "settings.financeLookups",
      },
      {
        to: "/settings/service-tax-codes",
        labelKey: "nav.serviceTaxCodes",
        descriptionKey: "settings.home.descriptions.serviceTaxCodes",
        icon: Receipt,
        permission: "settings.fiscal",
      },
      {
        to: "/settings/materials",
        labelKey: "nav.materials",
        descriptionKey: "settings.home.descriptions.materials",
        icon: Package,
        permission: "settings.materials",
      },
      {
        to: "/settings/equipments",
        labelKey: "nav.equipments",
        descriptionKey: "settings.home.descriptions.equipments",
        icon: Wrench,
        permission: "settings.equipments",
      },
      {
        to: "/settings/workers",
        labelKey: "nav.workers",
        descriptionKey: "settings.home.descriptions.workers",
        icon: HardHat,
        permission: "settings.workers",
      },
      {
        to: "/settings/project-priorities",
        labelKey: "nav.classifications",
        descriptionKey: "settings.home.descriptions.classifications",
        icon: Shapes,
        permission: "settings.lookups",
      },
    ],
  },
  {
    titleKey: "settings.sections.ferramentas",
    cards: [
      {
        to: "/settings/diary-report-models",
        labelKey: "diaryReports.settingsTitle",
        descriptionKey: "settings.home.descriptions.diaryReportModels",
        icon: Files,
        permission: "project.projectReport.manageModels",
      },
      {
        to: "/settings/project-service-fronts-models",
        labelKey: "nav.serviceFrontModels",
        descriptionKey: "settings.home.descriptions.serviceFrontModels",
        icon: Users,
        permission: "settings.serviceFrontModels",
      },
      {
        to: "/settings/checklist-models",
        labelKey: "nav.checklistModels",
        descriptionKey: "settings.home.descriptions.checklistModels",
        icon: ListChecks,
        permission: "settings.checklistModels",
      },
      {
        to: "/settings/occurrences",
        labelKey: "nav.occurrences",
        descriptionKey: "settings.home.descriptions.occurrences",
        icon: AlertTriangle,
        permission: "settings.occurrences",
      },
      {
        to: "/settings/document-categories",
        labelKey: "nav.documentCategories",
        descriptionKey: "settings.home.descriptions.documentCategories",
        icon: FileText,
        permission: "settings.documentCategories",
      },
      {
        to: "/settings/photo-tags",
        labelKey: "nav.photoTags",
        descriptionKey: "settings.home.descriptions.photoTags",
        icon: Tags,
        permission: "settings.photoTags",
      },
    ],
  },
];

/** Permission-filtered card sections for the `/settings` home page. */
export function getSettingsHomeSections(
  can: (permission: Permission) => boolean,
  options?: { showOrgBilling?: boolean },
): SettingsHomeSection[] {
  const showOrgBilling = options?.showOrgBilling ?? false;
  return SETTINGS_HOME_SECTIONS.map((section) => ({
    titleKey: section.titleKey,
    cards: section.cards.filter((card) => {
      if (!can(card.permission)) return false;
      if (card.requireOrgBilling && !showOrgBilling) return false;
      return true;
    }),
  })).filter((section) => section.cards.length > 0);
}
